import {
  isPlausibleBirthDate,
  type AnketaFormRequest,
  type ContactMethod,
  type HeardFrom,
  type Locale,
} from '@360face/domain';
import { DEFAULT_COUNTRY, toE164, type CountryCode } from '../kiosk/phone';

/** iPad client form (/anketa): one screen per step, the main action in the bottom rail. */
export type AnketaStep = 'welcome' | 'details' | 'prefs' | 'health' | 'sign' | 'done';
export const FORM_STEPS: AnketaStep[] = ['details', 'prefs', 'health', 'sign'];

export interface AnketaDraft {
  /** Country of the mobile number (picker as on the kiosk). */
  country: CountryCode;
  /** National digits; E.164 via `toE164`. */
  phone: string;
  firstName: string;
  lastName: string;
  /**
   * «Is this you?» (owner 28.10.2026): looked up after the phone — `none` = new client (types the
   * name), otherwise the card found for that phone (first name + initial only).
   */
  match?: 'none' | { token: string; name: string; emailMask?: string };
  /** The client answered «Yes, it's me» / «No». */
  confirmed?: boolean;
  rejected?: boolean;
  email: string;
  day: string;
  month: string;
  year: string;
  pressure?: number;
  contact?: ContactMethod;
  heardFrom?: HeardFrom;
  conditions?: 'none' | 'declared' | 'cleared';
  aesthetic?: 'none' | 'declared';
  /** Free comment for the studio (owner 28.10.2026) → the client card Note in Altegio. */
  comment: string;
  consent: boolean;
  privacy: boolean;
  marketing: boolean;
  signature?: string;
}

export const emptyDraft = (): AnketaDraft => ({
  country: DEFAULT_COUNTRY,
  phone: '',
  firstName: '',
  lastName: '',
  email: '',
  day: '',
  month: '',
  year: '',
  comment: '',
  consent: false,
  privacy: false,
  marketing: false,
});

/** Error keys map to messages (errPhone, errEmail…). */
export type FieldError =
  'errConfirm' | 'errPhone' | 'errEmail' | 'errBirth' | 'errRequired' | 'errChoose' | 'errConsent' | 'errSign';

export const isEmail = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());

/** YYYY-MM-DD from the three boxes, or undefined when not a plausible birth date (age 14–110). */
export function birthDateOf(d: Pick<AnketaDraft, 'day' | 'month' | 'year'>, today: string): string | undefined {
  if (!/^\d{1,2}$/.test(d.day) || !/^\d{1,2}$/.test(d.month) || !/^\d{4}$/.test(d.year)) return undefined;
  const iso = `${d.year}-${d.month.padStart(2, '0')}-${d.day.padStart(2, '0')}`;
  return isPlausibleBirthDate(iso, today) ? iso : undefined;
}

export function detailsErrors(
  d: AnketaDraft,
  today: string,
): Partial<Record<'phone' | 'firstName' | 'lastName' | 'email' | 'birth', FieldError>> {
  const e: Partial<Record<'phone' | 'firstName' | 'lastName' | 'email' | 'birth', FieldError>> = {};
  if (!toE164(d.country, d.phone)) e.phone = 'errPhone';
  // first only the phone; the rest once it is looked up (new client) or confirmed (owner 28.10.2026)
  if (d.match === undefined || (d.match !== 'none' && !d.confirmed)) return e;
  const known = recognizedToken(d) !== undefined;
  if (!known && !d.firstName.trim()) e.firstName = 'errRequired';
  if (!known && !d.lastName.trim()) e.lastName = 'errRequired';
  if (!(known && emailOnFile(d)) && d.email.trim() && !isEmail(d.email)) e.email = 'errEmail';
  if (!birthDateOf(d, today)) e.birth = 'errBirth';
  return e;
}

/** The token of a confirmed «Yes, it's me»; undefined for a new client. */
export function recognizedToken(d: AnketaDraft): string | undefined {
  return d.confirmed && d.match && d.match !== 'none' ? d.match.token : undefined;
}

/** A confirmed client whose card already has an e-mail: the form does not ask for it again. */
export function emailOnFile(d: AnketaDraft): boolean {
  return Boolean(recognizedToken(d) && d.match && d.match !== 'none' && d.match.emailMask);
}

/** v4 (owner 19.10.2026): one confirmation — "none applies, I'll tell the therapist otherwise". */
export function healthErrors(d: AnketaDraft): Partial<Record<'conditions', FieldError>> {
  return d.conditions === 'none' && d.aesthetic === 'none' ? {} : { conditions: 'errConfirm' };
}

export function signErrors(d: AnketaDraft): Partial<Record<'consent' | 'signature', FieldError>> {
  return {
    ...(d.consent && d.privacy ? {} : { consent: 'errConsent' as const }),
    ...(d.signature ? {} : { signature: 'errSign' as const }),
  };
}

/** The errors of one step (prefs has none: everything there is optional). */
export function stepErrors(step: AnketaStep, d: AnketaDraft, today: string): Record<string, FieldError> {
  if (step === 'details') return detailsErrors(d, today);
  if (step === 'health') return healthErrors(d);
  if (step === 'sign') return signErrors(d);
  return {};
}

export function nextStep(step: AnketaStep): AnketaStep {
  const i = FORM_STEPS.indexOf(step);
  return i >= 0 && i < FORM_STEPS.length - 1 ? FORM_STEPS[i + 1]! : 'done';
}

export function prevStep(step: AnketaStep): AnketaStep {
  const i = FORM_STEPS.indexOf(step);
  return i > 0 ? FORM_STEPS[i - 1]! : 'welcome';
}

/** Request for POST /v1/locations/:id/anketa — only after every step is valid. */
export function toRequest(d: AnketaDraft, version: string, locale: Locale, today: string): AnketaFormRequest {
  const birthDate = birthDateOf(d, today);
  const phone = toE164(d.country, d.phone);
  if (!phone || !birthDate || !d.conditions || !d.aesthetic || !d.signature || !d.consent || !d.privacy) {
    throw new Error('anketa draft incomplete');
  }
  const token = recognizedToken(d);
  const email = token && emailOnFile(d) ? '' : d.email.trim().toLowerCase();
  const comment = d.comment.replace(/\s+/g, ' ').trim();
  return {
    version,
    locale,
    ...(token ? { recognized: token } : { firstName: d.firstName.trim(), lastName: d.lastName.trim() }),
    phone,
    ...(email ? { email } : {}),
    birthDate,
    ...(d.contact ? { contact: d.contact } : {}),
    ...(d.pressure ? { pressure: d.pressure } : {}),
    ...(d.heardFrom ? { heardFrom: d.heardFrom } : {}),
    conditions: d.conditions,
    aesthetic: d.aesthetic,
    ...(comment ? { comment } : {}),
    consent: true,
    privacy: true,
    marketing: d.marketing,
    signature: d.signature,
  };
}

/** Local date YYYY-MM-DD (the iPad's clock, for the birth-date check only). */
export function localToday(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}
